import{createTheme}from'@mui/material/styles';

const baseTokens={
 primary:'#0F5F63',
 primaryHover:'#147477',
 navy:'#1D384A',
 gold:'#C8A96B',
 sand:'#F4F1EA',
 ivory:'#FCFBF7',
 stone:'#D8D0C4',
 textSecondary:'#52636F',
 paper:'#FFFFFF',
 success:'#28784B',
 warning:'#946200',
 error:'#B42318',
 info:'#25637A',
 navActive:'rgba(15,95,99,.32)',
 navHover:'rgba(255,255,255,.06)',
 borderSoft:'rgba(216,208,196,.72)',
 shadow:'0 10px 30px rgba(29,56,74,.07)',
 shadowSoft:'0 2px 10px rgba(29,56,74,.05)'
}as const;
// Legacy page-level tokens resolve to the current appearance using CSS variables.
export const rasanaTokens={...baseTokens,
 primary:'var(--rasana-primary)',primaryHover:'var(--rasana-primary-hover)',
 navy:'var(--rasana-navy)',gold:'var(--rasana-gold)',
 sand:'var(--rasana-sand)',ivory:'var(--rasana-ivory)',stone:'var(--rasana-stone)',
 textSecondary:'var(--rasana-text-secondary)',paper:'var(--rasana-paper)',
 borderSoft:'var(--rasana-border-soft)'
} as const;

export type RasanaAppearance='momentum'|'calm'|'intelligent';
export const appearanceKey='rasana.login.theme.v1';
export const appearancePalettes={
 momentum:{primary:'#147C81',primaryHover:'#0F646A',navy:'#102D3B',gold:'#D3A858',sand:'#EAF1F0',ivory:'#F4F8F7',stone:'#C4D6D7',textSecondary:'#48636E',paper:'#FFFFFF'},
 calm:{primary:'#0F5F63',primaryHover:'#147477',navy:'#1D384A',gold:'#C8A96B',sand:'#F4F1EA',ivory:'#FCFBF7',stone:'#D8D0C4',textSecondary:'#52636F',paper:'#FFFFFF'},
 intelligent:{primary:'#235B9A',primaryHover:'#174A81',navy:'#162B4E',gold:'#45BDAE',sand:'#E8F1FA',ivory:'#F4F8FF',stone:'#C9D7EA',textSecondary:'#4C6281',paper:'#FFFFFF'}
} as const;
export function loadAppearance():RasanaAppearance{
 try{const value=localStorage.getItem(appearanceKey);
  if(value==='momentum'||value==='calm'||value==='intelligent')return value;
 }catch{}
 return'momentum';
}
export function saveAppearance(value:RasanaAppearance){
 try{localStorage.setItem(appearanceKey,value);}catch{}
 window.dispatchEvent(new Event('rasana-appearance-changed'));
}
export function createRasanaTheme(appearance:RasanaAppearance){
 const selected=appearancePalettes[appearance];
 const rasanaTokens={...baseTokens,...selected};
 return createTheme({
 palette:{
  mode:'light',
  primary:{
   main:rasanaTokens.primary,
   dark:rasanaTokens.primaryHover,
   contrastText:'#FFFFFF'
  },
  secondary:{
   main:rasanaTokens.navy,
   contrastText:'#FFFFFF'
  },
  background:{
   default:rasanaTokens.ivory,
   paper:rasanaTokens.paper
  },
  text:{
   primary:rasanaTokens.navy,
   secondary:rasanaTokens.textSecondary
  },
  divider:rasanaTokens.stone,
  success:{main:rasanaTokens.success},
  warning:{main:rasanaTokens.warning},
  error:{main:rasanaTokens.error},
  info:{main:rasanaTokens.info}
 },
 shape:{borderRadius:8},
 spacing:8,
 typography:{
  fontFamily:'Inter, "Segoe UI", Arial, sans-serif',
  fontSize:14,
  h1:{fontSize:32,fontWeight:600,lineHeight:1.2,letterSpacing:'-.02em'},
  h2:{fontSize:28,fontWeight:600,lineHeight:1.25,letterSpacing:'-.015em'},
  h3:{fontSize:26,fontWeight:600,lineHeight:1.25,letterSpacing:'-.01em'},
  h4:{fontSize:24,fontWeight:600,lineHeight:1.3,letterSpacing:'-.01em'},
  h5:{fontSize:20,fontWeight:600,lineHeight:1.35},
  h6:{fontSize:18,fontWeight:600,lineHeight:1.4},
  subtitle1:{fontSize:16,fontWeight:600},
  subtitle2:{fontSize:14,fontWeight:600},
  body1:{fontSize:14,lineHeight:1.55},
  body2:{fontSize:13.5,lineHeight:1.5},
  caption:{fontSize:12,lineHeight:1.45},
  overline:{fontSize:11,fontWeight:600,letterSpacing:'.14em',lineHeight:1.5},
  button:{textTransform:'none',fontWeight:600,letterSpacing:0}
 },
 components:{
  MuiCssBaseline:{
   styleOverrides:{
    ':root':{
     colorScheme:'light',
     '--rasana-primary':selected.primary,
     '--rasana-primary-hover':selected.primaryHover,
     '--rasana-navy':selected.navy,
     '--rasana-gold':selected.gold,
     '--rasana-sand':selected.sand,
     '--rasana-ivory':selected.ivory,
     '--rasana-stone':selected.stone,
     '--rasana-text-secondary':selected.textSecondary,
     '--rasana-paper':selected.paper,
     '--rasana-border-soft':selected.stone
    },
    body:{
     backgroundColor:rasanaTokens.ivory,
     color:rasanaTokens.navy,
     WebkitFontSmoothing:'antialiased',
     MozOsxFontSmoothing:'grayscale'
    },
    '::selection':{backgroundColor:'rgba(15,95,99,.18)'},
     'input[type="password"]::-ms-reveal, input[type="password"]::-ms-clear':{display:'none !important'}
   }
  },
  MuiPaper:{
   styleOverrides:{
    root:{
     backgroundImage:'none'
    },
    outlined:{
     borderColor:rasanaTokens.stone
    }
   }
  },
  MuiCard:{
   styleOverrides:{
    root:{
     border:`1px solid ${rasanaTokens.borderSoft}`,
     boxShadow:'0 1px 6px rgba(29,56,74,.035)',
     backgroundImage:'none'
    }
   }
  },
  MuiCardContent:{
   styleOverrides:{
    root:{
     padding:20,
     '&:last-child':{paddingBottom:20},
     '@media (max-width:599.95px)':{
      padding:16,
      '&:last-child':{paddingBottom:16}
     }
    }
   }
  },
  MuiButton:{
   defaultProps:{
    disableElevation:true
   },
   styleOverrides:{
    root:{
     borderRadius:6,
     minHeight:40,
     paddingInline:12,
     '@media (max-width:599.95px)':{
      minHeight:44,whiteSpace:'normal',overflowWrap:'anywhere'
     },
     '&:focus-visible':{outline:'3px solid #C8A96B',outlineOffset:2}
    },
    containedPrimary:{
     '&:hover':{
      backgroundColor:rasanaTokens.primaryHover,
      color:'#FFFFFF'
     }
    },
    containedSecondary:{'&:hover':{backgroundColor:'#335B73',color:'#FFFFFF'}},
    text:{'&:hover':{backgroundColor:'rgba(15,95,99,.13)',textDecoration:'underline'}},
    outlined:{
     borderColor:rasanaTokens.stone,
     '&:hover':{
      borderColor:rasanaTokens.primary,
      backgroundColor:'rgba(15,95,99,.12)'
     }
    }
   }
  },
  MuiIconButton:{
   styleOverrides:{
    root:{
     borderRadius:6,
     '@media (max-width:599.95px)':{minWidth:44,minHeight:44}
    }
   }
  },
  MuiTextField:{
   defaultProps:{size:'small'}
  },
  MuiDialogActions:{
   styleOverrides:{
    root:{
     flexWrap:'wrap',
     gap:8,
     padding:16,
     borderTop:`1px solid ${rasanaTokens.borderSoft}`,
     backgroundColor:rasanaTokens.paper,
     position:'sticky',
     bottom:0,
     zIndex:2,
     '& .MuiButton-root':{minHeight:44,fontWeight:650},
     '@media (max-width:599.95px)':{
      padding:12,display:'flex',flexDirection:'column-reverse',
      alignItems:'stretch',gap:8,
      '& > *':{margin:'0 !important',minWidth:0},
      '& > .MuiButton-root':{width:'100%',minHeight:44,flexGrow:0,
       whiteSpace:'normal',textAlign:'center'},
      '& > .MuiStack-root':{
       flexDirection:'column-reverse',alignItems:'stretch',gap:1,
       width:'100%',
       '& .MuiButton-root':{width:'100%',margin:'0 !important'}
      }
     }
    }
   }
  },
  MuiFormHelperText:{
   styleOverrides:{root:{fontSize:12,marginTop:5,lineHeight:1.45}}
  },
  MuiOutlinedInput:{
   styleOverrides:{
    root:{
     minHeight:40,
     borderRadius:6,
     backgroundColor:rasanaTokens.paper,
     '&:hover .MuiOutlinedInput-notchedOutline':{
      borderColor:'#AFA79B'
     },
     '&.Mui-focused .MuiOutlinedInput-notchedOutline':{
      borderWidth:1.5
     }
    },
    notchedOutline:{
     borderColor:rasanaTokens.stone
    }
   }
  },
  MuiInputLabel:{
   styleOverrides:{
    root:{
     fontSize:14,
     '& .MuiFormLabel-asterisk':{color:'#B53B38',fontWeight:700},
     '&.Mui-error':{color:'#A62727'}
    }
   }
  },
  MuiChip:{
   styleOverrides:{
    root:{
     minHeight:26,
     borderRadius:16,
     fontWeight:600
    }
   }
  },
  MuiAlert:{
   styleOverrides:{
    root:{
     borderRadius:8,
     border:'1px solid currentColor'
    }
   }
  },
  MuiDivider:{
   styleOverrides:{
    root:{
     borderColor:rasanaTokens.borderSoft
    }
   }
  },
  MuiTable:{
   styleOverrides:{
    root:{
     backgroundColor:rasanaTokens.paper
    }
   }
  },
  MuiTableHead:{
   styleOverrides:{
    root:{
     backgroundColor:rasanaTokens.sand
    }
   }
  },
  MuiTableCell:{
   styleOverrides:{
    head:{
     color:rasanaTokens.navy,
     fontSize:12.5,
     fontWeight:600,
     letterSpacing:'.02em'
    },
    root:{
     borderBottom:`1px solid ${rasanaTokens.borderSoft}`
    }
   }
  },
  MuiTabs:{
   styleOverrides:{
    root:{
     minHeight:42,
     borderBottom:`1px solid ${rasanaTokens.borderSoft}`
    },
    indicator:{
     height:2
    }
   }
  },
  MuiTab:{
   styleOverrides:{
    root:{
     minHeight:42,
     textTransform:'none',
     fontWeight:600,
     color:rasanaTokens.textSecondary,
     '&.Mui-selected':{
      color:rasanaTokens.primary
     }
    }
   }
  },
  MuiDialog:{
   styleOverrides:{
    paper:{
     borderRadius:10,
     border:`1px solid ${rasanaTokens.borderSoft}`,
     boxShadow:rasanaTokens.shadow
    }
   }
  },
  MuiDrawer:{
   styleOverrides:{
    paper:{
     backgroundImage:'none'
    }
   }
  },
  MuiTooltip:{
   styleOverrides:{
    tooltip:{
     backgroundColor:rasanaTokens.navy,
     fontSize:12,
     borderRadius:6
    }
   }
  }
 }
 });
}
export const theme=createRasanaTheme('calm');
